import formatInTimeZone from 'date-fns-tz/formatInTimeZone'
import format from 'date-fns/format'
import enUS from 'date-fns/locale/en-US'
import fr from 'date-fns/locale/fr'
import i18n from 'src/i18n'
import isEqual from 'date-fns/isEqual'

// French dates only take an ordinal on the 1st (e.g. "1er janvier", "2 janvier")
const frDate = {
  ...fr,
  localize: {
    ...fr.localize,
    ordinalNumber: (n, opts) =>
      opts?.unit === 'date' && Number(n) !== 1 ? String(n) : fr.localize.ordinalNumber(n, opts),
  },
}

const dateFnsLocales = { en: enUS, fr: frDate }

// Returns the active date-fns locale based on the current i18n language.
export const getDateFnsLocale = () => dateFnsLocales[i18n.language?.split('-')[0]] ?? enUS

const dateConfig = {
  en: {
    monthDay: 'MMM d',
    monthDayOrdinal: 'MMM do',
    fullMonthDayOrdinal: 'MMMM do',
    monthDayYear: 'MMM dd, yyyy',
    weekdayMonthDay: 'EEE, MMM d',
    weekdayMonthDayOrdinal: 'EEE, MMM do',
    fullWeekdayMonthDayOrdinal: 'EEEE, MMM do',
    weekdayFullMonthDayOrdinal: 'EEE, MMMM do',
    fullWeekdayMonthDayYear: 'EEEE, MMM d, yyyy',
    monthFirst: true,
  },
  fr: {
    monthDay: 'd MMM',
    monthDayOrdinal: 'do MMM',
    fullMonthDayOrdinal: 'do MMMM',
    monthDayYear: 'dd MMM, yyyy',
    weekdayMonthDay: 'EEE, d MMM',
    weekdayMonthDayOrdinal: 'EEE, do MMM',
    fullWeekdayMonthDayOrdinal: 'EEEE, do MMM',
    weekdayFullMonthDayOrdinal: 'EEE, do MMMM',
    fullWeekdayMonthDayYear: 'EEEE, d MMM, yyyy',
    monthFirst: false,
  },
}

const getConfig = () => dateConfig[i18n.language?.split('-')[0]] ?? dateConfig.en
export const getMonthDayFormat = () => getConfig().monthDay
export const getMonthDayOrdinalFormat = () => getConfig().monthDayOrdinal
export const getFullMonthDayOrdinalFormat = () => getConfig().fullMonthDayOrdinal
export const getMonthDayYearFormat = () => getConfig().monthDayYear
export const getWeekdayMonthDayFormat = () => getConfig().weekdayMonthDay
export const getWeekdayMonthDayOrdinalFormat = () => getConfig().weekdayMonthDayOrdinal
export const getFullWeekdayMonthDayOrdinalFormat = () => getConfig().fullWeekdayMonthDayOrdinal
export const getWeekdayFullMonthDayOrdinalFormat = () => getConfig().weekdayFullMonthDayOrdinal
export const getFullWeekdayMonthDayYearFormat = () => getConfig().fullWeekdayMonthDayYear
export const getFullWeekdayMonthDayYearOrdinalFormat = () => getConfig().fullWeekdayMonthDayYearOrdinal

export const formatDateRange = (
  startDate,
  startOffset,
  endDate,
  endOffset,
  dateFormat = getFullWeekdayMonthDayYearFormat(),
  sameMonthDayFormat = '', // the "d" or "do" format
  sameMonth = false,
) => {
  const monthFirst = getConfig().monthFirst

  const isRange = endDate && !isEqual(startDate, endDate)
  const trim = isRange && sameMonth // only shorten when there's an actual range in the same month

  // EN (month first): trim the END  -> "Nov 8 - 22"
  // FR (month last):  trim the START -> "8 - 22 nov."
  const startFormat = trim && !monthFirst ? sameMonthDayFormat : dateFormat
  const start = formatWithTZ(startDate, startOffset, startFormat)

  if (!endDate || isEqual(startDate, endDate)) return start

  const endFormat = trim && monthFirst ? sameMonthDayFormat : dateFormat
  const end = formatWithTZ(endDate, endOffset, endFormat)

  return `${start} - ${end}`
}

// Override date-fns timezone format
export const formatAMPM = (date) => {
  let hours = date.getHours()
  let minutes = date.getMinutes()
  let ampm = hours >= 12 ? 'pm' : 'am'
  hours = hours % 12
  hours = hours ? hours : 12
  minutes = minutes.toString().padStart(2, '0')
  let strTime = hours + ':' + minutes + ' ' + ampm
  return strTime
}

// const array of months
export const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const formatDAMPM = (date) => {
  let month = getDateFnsLocale().localize.month(date.getMonth(), { width: 'abbreviated' })
  let day = date.getDate()
  day = day < 10 ? '0' + day : day
  let hours = date.getHours()
  let minutes = date.getMinutes()
  let ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12
  hours = hours ? hours : 12
  minutes = minutes.toString().padStart(2, '0')
  let strTime = month + ' ' + day + ' ' + hours + ':' + minutes + ' ' + ampm
  return strTime
}

export const formatWithTZ = (date, timezoneOffset = '+00:00', formatString = 'EEEE, MMM d, yyyy') => {
  let formattedDate = ''
  const locale = getDateFnsLocale()
  try {
    formattedDate = formatInTimeZone(date, timezoneOffset, formatString, { locale })
  } catch (e) {
    console.error('TZ FORMAT ERRROR:', e)
    formattedDate = format(date, formatString, { locale })
  }
  return formattedDate
}
